import { useEffect, useState } from 'react';

import axios, { AxiosResponse } from 'axios';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { LOCALE_API } from '../config/apiPaths';
import { LocalizationFile } from '../localization/parser';
import { getUrls } from '../utils/url';
import useLocalStorage from './useLocalStorage';

interface Locale {
    [key: string]: any;
}

interface I18nStatus {
    loading: boolean;
    error: unknown | null;
    i18n: typeof i18n;
}

// const TIME_TO_CACHE = 1000 * 60 * 60 * 24; // 24 hours
const TIME_TO_CACHE = 0; // 0 hours

const useI18Next = () => {
    const initialStatus = { loading: true, error: null, i18n };

    const [status, setStatus] = useState<I18nStatus>(initialStatus);
    const [locale, setLocale] = useLocalStorage<Locale>('cfz_locale', {});
    const [fallbackLocale, setFallbackLocale] = useLocalStorage<Locale>('cfz_fallback_locale', {});
    // const [expiration, setExpiration] = useLocalStorage('cfz_locale_expiration', new Date().getTime());
    const [expiration, setExpiration] = useLocalStorage('cfz_locale_expiration', 0);

    useEffect(() => {
        const { api } = getUrls();
        const defaultLang = 'en';
        let { language } = window.navigator;

        if (language.length > 2) {
            language = language.substr(0, 2);
        }

        const getTranslations = async () => {
            try {
                let translation = locale;
                let fallbackTranslation = fallbackLocale;

                if (new Date(Number(expiration)) < new Date() || Object.keys(translation).length === 0) {
                    const [{ data: localeResponse }, { data: fallbackResponse }] = await Promise.all([
                        axios.get<LocalizationFile>(`${api}${LOCALE_API}?lang=${language}`).catch(() => ({ data: {} } as AxiosResponse)),
                        axios.get<LocalizationFile>(`${api}${LOCALE_API}?lang=${defaultLang}`)
                    ]);

                    translation = localeResponse;
                    fallbackTranslation = fallbackResponse;
                    setLocale(translation);
                    setFallbackLocale(fallbackTranslation);
                    setExpiration(new Date().getTime() + TIME_TO_CACHE);
                }

                await i18n
                    .use(LanguageDetector)
                    .use(initReactI18next)
                    .init({
                        fallbackLng: defaultLang,
                        defaultNS: 'common',
                        ns: ['common'],
                        resources: {
                            [language]: translation,
                            [defaultLang]: fallbackTranslation
                        },
                        detection: {
                            order: ['navigator']
                        },
                        react: {
                            useSuspense: false
                        }
                    });

                setStatus({ loading: false, error: null, i18n });
            } catch (err) {
                setStatus({ loading: false, error: err, i18n });
            }
        };

        getTranslations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return status;
};

export default useI18Next;
